import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  List,       // Keep if you decide to show few donations on hover
  ListItem,   // Keep
  ListItemText, // Keep
  Divider,    // Keep
  Link,       // Import Link for the "View All" button
} from '@mui/material';
import DonateDialog from '../Donate/DonateDialog';
import AllDonationsModal from './AllDonationsModal'; // Import the new modal component
import COLORS from '../../../../../../constants/colors';
import baseURL from '../../../../user/config';

// Helper function: Formats amount to Ugandan Shillings
const formatAmount = (amount) => {
  if (typeof amount !== 'number') return 'UGX 0'; // Handle non-numeric input gracefully
  return 'UGX ' + Math.floor(amount).toLocaleString();
};

const DonationWidget = ({ campaign, onDonate }) => {
  const [openDonateDialog, setOpenDonateDialog] = useState(false); // Renamed for clarity
  const [openAllDonationsModal, setOpenAllDonationsModal] = useState(false); // New state for All Donations modal
  const [recentDonations, setRecentDonations] = useState([]);
  const [loadingDonations, setLoadingDonations] = useState(true);
  const [lastDonation, setLastDonation] = useState(null); // Keep if you want to use it for an animation/toast later

  // Removed the duplicate formatAmount function here

  const fetchDonations = async () => {
    setLoadingDonations(true); // Set loading before fetch
    try {
      const res = await fetch(`${baseURL}/data/backend/donations/recent?campaignId=${campaign.id}`);
      const data = await res.json();
      setRecentDonations(data);
    } catch (err) {
      console.error('Error loading donations:', err);
    } finally {
      setLoadingDonations(false);
    }
  };

  // --- fmt function remains as originally provided ---
const fmt = (amt) => 'UGX ' + Math.floor(amt).toLocaleString();

const sentenceCase = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';


  
  // Helper function: Formats timestamp to a more readable date
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };




  // Effect to fetch donation data
  useEffect(() => {
    if (campaign && campaign.id) {
      fetchDonations();
    }
  }, [campaign]);

  const progressPercentage = campaign && campaign.goal > 0
    ? ((campaign.raised / campaign.goal) * 100).toFixed(2)
    : "0.00";

  // Re-introducing slice to show only a few donations on hover for the widget
  // The full list will be in the new modal.
  const displayedRecentDonations = recentDonations.slice(0, 3);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1000,
      }}
    >
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.09)',
          bgcolor: 'background.paper',
          transition: 'width 0.3s ease-in-out, max-height 0.3s ease-in-out, padding 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          width: 200, // Default width (collapsed)
          maxHeight: 280, // Default max-height (collapsed) - adjusted to fit button + progress
          overflow: 'hidden', // Hide overflow content when collapsed
          p: 2, // Default padding (collapsed)
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between', // Push button to bottom
          '&:hover': {
            width: 280, // Expanded width on hover
            maxHeight: '90vh', // Allow more height on hover for scrollable content
            boxShadow: '0 12px 30px rgba(0,0,0,0.15)', // More prominent shadow on hover
            p: 3, // More padding on hover
            // The main card doesn't need overflowY: 'auto' here anymore.
            // The scroll is for the list within its own Box (if needed) or in the modal.
          },
          // Custom scrollbar for better aesthetics - moved to the AllDonationsModal
        }}
      >
        <CardContent sx={{
          p: 0, // Reset default CardContent padding
          flexGrow: 1, // Allows content to grow, pushing button to bottom
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start', // Align content to top
          '&:last-child': { pb: 0 } // Remove default bottom padding
        }}>
          {/* Always visible header content (campaign name, progress, amounts) */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            textAlign: 'left',
            mb: 2, // Consistent margin bottom
            flexShrink: 0, // Prevent this header from shrinking
          }}>
            <Typography variant="subtitle2" color={COLORS.textSecondary} sx={{ fontWeight: 'bold' }} gutterBottom>
              {sentenceCase(campaign.name)}
            </Typography>

            {/* Progress Percentage */}
            <Typography
              variant="h6" // Consistent size for progress percentage
              color={COLORS.primary}
              sx={{ mb: 0.5 }}
            >
              {progressPercentage}% funded
            </Typography>

            <Typography variant="caption" color={COLORS.textSecondary}>
              <strong>{fmt(campaign.raised)}</strong> raised
              of <strong>{fmt(campaign.goal)}</strong>
            </Typography>

            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 5,
                backgroundColor: COLORS.border,
                '& .MuiLinearProgress-bar': { backgroundColor: COLORS.primary },
                width: '100%',
                mt: 1,
                mb: 1.5,
              }}
            />
          </Box>

          {/* Collapsible content (Recent Donations Section) - now shows sliced list */}
          <Box
            sx={{
              maxHeight: 0, // Hidden by default
              overflow: 'hidden', // Hide overflow initially
              opacity: 0, // Start invisible
              transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
              '.MuiCard-root:hover &': { // Apply styles when parent Card is hovered
                maxHeight: 'calc(90vh - 200px)', // Adjust this value to accommodate content
                opacity: 1, // Become visible on hover
                py: 1, // Add vertical padding to the content area
                // If displayedRecentDonations (3 items) don't fit, add overflowY: 'auto' here
                // overflowY: 'auto', // Uncomment if even 3 items might overflow the small hover area
              },
            }}
          >
            <Divider sx={{ my: 2, borderColor: COLORS.border }} />

            <Typography variant="h6" fontWeight="bold" color={COLORS.primary} mt={2} mb={1}>
              Recent Donations
            </Typography>

            {loadingDonations ? (
              <Box>
                {[...Array(3)].map((_, i) => (
                  <Box key={i} display="flex" alignItems="center" mb={1}>
                    <Box flexGrow={1}>
                      <Typography variant="body2" sx={{ bgcolor: COLORS.border, height: 18, borderRadius: '4px', width: '80%' }}></Typography>
                      <Typography variant="caption" sx={{ bgcolor: COLORS.border, height: 12, borderRadius: '4px', width: '60%', mt: 0.5 }}></Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : recentDonations.length === 0 ? (
              <Typography variant="body2" color={COLORS.textSecondary}>
                No donations yet. Be the first to contribute!
              </Typography>
            ) : (
              <List disablePadding sx={{ width: '100%' }}>
                {displayedRecentDonations.map((donation) => ( // Using the sliced array here
                  <ListItem key={donation.id} disableGutters sx={{ mb: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color={COLORS.text}>
                          {fmt(donation.amount)} from{' '}
                          <Typography component="span" fontWeight="bold" color={COLORS.primary}>
                            {donation.donor}
                          </Typography>
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(donation.timestamp)}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* "View All Donations" Link - now opens the modal */}
            {recentDonations.length > 0 && ( // Only show if there are any donations at all
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setOpenAllDonationsModal(true)} // Open the new modal
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: COLORS.primaryDark,
                    },
                  }}
                >
                  View All Donations
                </Link>
              </Box>
            )}
          </Box>
        </CardContent>

        {/* Donate Button - ALWAYS at the bottom of the Card, OUTSIDE collapsible content */}
        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: COLORS.accent,
            color: COLORS.white,
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            boxShadow: 0,
            fontSize: '1rem',
            fontWeight: 'bold',
            textTransform: 'none',
            '&:hover': {
              boxShadow: 0,
              backgroundColor: COLORS.accentDark,
            },
            mt: 2,
            flexShrink: 0,
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent any parent hover/click effects from triggering
            setOpenDonateDialog(true);
          }}
        >
          Donate Now
        </Button>
      </Card>

      {/* DonateDialog Component */}
      <DonateDialog
        open={openDonateDialog}
        onClose={() => setOpenDonateDialog(false)}
        campaign={campaign}
        onDonate={onDonate}
      />

      {/* AllDonationsModal Component (the new modal) */}
      <AllDonationsModal
        open={openAllDonationsModal}
        onClose={() => setOpenAllDonationsModal(false)}
        donations={recentDonations} // Pass the full list of donations
        formatAmount={formatAmount}
        formatTimestamp={formatTimestamp}
        campaignName={campaign.name} // Pass campaign name for modal title
      />
    </Box>
  );
};

export default DonationWidget;